import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Inbox, Plus } from "lucide-react";
import { useAppSelector } from "../../app/hooks";
import { selectUpcomingSessions } from "../../features/sessions/selectors/upcomingSessionsSelectors";
import { selectInboxCount } from "../../features/trial-requests/trial-requests-slice";
import { convertSessionTimeToUserTimezone } from "../../features/utils/timezoneUtils";
import { formatTimeDisplay } from "../../features/sessions/utils/DisplayMappers";
import { ROUTES } from "../../routes";
import ShareLinksButton from "../members/ShareLinksButton";

/**
 * Dashboard header strip: today's schedule in one line, an Inbox chip when
 * something is waiting, and the two most common quick actions. Reads state
 * other dashboard widgets already load (upcoming sessions, inbox count) —
 * no fetches of its own.
 */
const HomeOverviewStrip = () => {
  const { t } = useTranslation();
  const upcomingSessions = useAppSelector(selectUpcomingSessions);
  const inboxCount = useAppSelector(selectInboxCount);

  const today = useMemo(() => {
    const now = new Date();
    // The instant math uses the session's own time zone; the label below shows
    // the session's wall-clock time, consistent with the calendar and detail.
    const todaysSessions = upcomingSessions
      .map((session) => ({
        session,
        start: convertSessionTimeToUserTimezone(
          session.dateStart,
          session.timeLocal,
          session.timeZone || "UTC",
          session.duration,
        ).userTimezoneDate,
      }))
      .filter(({ start }) => start.toDateString() === now.toDateString());
    const next = todaysSessions
      .filter(({ start }) => start.getTime() >= now.getTime())
      .sort((a, b) => a.start.getTime() - b.start.getTime())[0];
    return { count: todaysSessions.length, next: next?.session };
  }, [upcomingSessions]);

  const nextTimeLabel = today.next ? formatTimeDisplay(today.next.timeLocal) : null;

  return (
    <div className="flex flex-wrap items-center gap-3">
      <p className="text-sm text-grey-40 dark:text-grey-80">
        {today.count > 0
          ? `${t("home.todaySessions", "Sessions today: {{count}}", { count: today.count })}${
              nextTimeLabel
                ? ` · ${t("home.todayNextAt", "Next at {{time}}", { time: nextTimeLabel })}`
                : ""
            }`
          : t("home.todayNoSessions", "No sessions today.")}
      </p>
      <div className="ms-auto flex flex-wrap items-center gap-2">
        {/* The portal and booking-page links to hand on (PD-0057) */}
        <ShareLinksButton testId="home-share-links" />
        {inboxCount > 0 && (
          <Link to={ROUTES.TRIAL_REQUESTS} className="btn btn-secondary">
            <Inbox size={16} strokeWidth={1.5} />
            <span>{t("home.inboxWaiting", "{{count}} in your Inbox", { count: inboxCount })}</span>
          </Link>
        )}
        <Link to={ROUTES.CREATE_MEMBER} className="btn btn-secondary">
          <Plus size={16} />
          <span>{t("members.newMember")}</span>
        </Link>
        <Link to={ROUTES.CREATE_SESSION} className="btn btn-primary">
          <Plus size={16} />
          <span>{t("navigation.newSession")}</span>
        </Link>
      </div>
    </div>
  );
};

export default HomeOverviewStrip;
